import React, { useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  DirectionalHint,
  TooltipDelay,
} from "@fluentui/react";
import { Enums } from "@cornerstonejs/core";
import {
  PET_COLORMAPS,
  applyPETFusion,
  removePETFusion,
  prepareFusionSeries,
  selectFusableSeries,
  stopOverlayPrefetch,
} from "../../../helpers/ColorLUTService";
import toastContext from "../../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../../constants/Constants";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import ColorLutIcon from "../../../../../components/SVGComponents/Tools/ColorLutIcon";
import ModalMain from "../../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../../components/Modal/ModalFooter";
import ColorLUTBody from "./ColorLUTBody";
import ColorLUTOverlay from "./ColorLUTOverlay";
import themeContext from "../../../../../context/theme/ThemeContext";
import studyviewerContext from "../../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import {
  toolBarFlex,
  toolTipStyles,
} from "../../../styles/styles";
import { getViewport } from "../../../helpers/CornerstoneViewportService";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import "../../../styles/ColorLUTTool.css";

// First colormap in the PET_COLORMAPS list is the default
const DEFAULT_COLORMAP = Object.values(PET_COLORMAPS)[0];
const DEFAULT_OPACITY = 0.5;

// Custom events for mutual exclusivity with the Color LUT tool
const FUSION_ACTIVATED_EVENT = 'viewer:fusion-activated';
const COLORLUT_ACTIVATED_EVENT = 'viewer:colorlut-activated';
// Matches the dropdown option key in ColorLUTOverlay.jsx
const FUSION_NONE_KEY = "__none__";

const ColorLUTTool = () => {
  const { t } = useTranslation();
  const { showToastv2 } = useContext(toastContext);
  const { theme } = useContext(themeContext);
  const { studyData, priorStudyData } = useContext(studyviewerContext);
  const { activeViewportIndex, viewportSeriesData } = useContext(ViewportContext);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Fusion state
  const [isFusionActive, setIsFusionActive] = useState(false);
  // { read, total } while both series are being pulled down, null once ready.
  const [preparation, setPreparation] = useState(null);
  const preparationRef = useRef(null);
  // Which viewport the loader belongs over, known from the click — before
  // fusion has been applied and fusionAppliedViewportRef has been set.
  const preparingViewportRef = useRef(null);
  const isPreparing = preparation != null;

  /** Turn a failure reason from applyPETFusion into something readable. */
  const fusionFailureMessage = useCallback(
    (reason) => {
      switch (reason) {
        case "no-series":
          return t("viewer.tools.fusion-failed-no-series", "That series has no images to fuse");
        case "no-base-image":
          return t(
            "viewer.tools.fusion-failed-no-base",
            "The viewport has no image to fuse onto"
          );
        case "no-overlay-slice":
          return t(
            "viewer.tools.fusion-failed-no-slice",
            "No matching slice in the selected series"
          );
        case "overlay-unreadable":
          return t(
            "viewer.tools.fusion-failed-unreadable",
            "Could not read the selected series"
          );
        default:
          return t("viewer.tools.fusion-failed", "Failed to apply fusion");
      }
    },
    [t]
  );

  /** Abandon an in-flight series download — fusion is going away or changing. */
  const cancelPreparation = useCallback(() => {
    preparationRef.current?.cancel();
    preparationRef.current = null;
    preparingViewportRef.current = null;
    setPreparation(null);
  }, []);

  const [fusionColormap, setFusionColormap] = useState(DEFAULT_COLORMAP);
  const [fusionOpacity, setFusionOpacity] = useState(DEFAULT_OPACITY);
  const [selectedSeries, setSelectedSeries] = useState(null);

  // Track viewport-box DOM element for portal
  const [portalTarget, setPortalTarget] = useState(null);
  const prevViewportRef = useRef(activeViewportIndex);
  const fusionBaseSeriesRef = useRef(null);

  // Always-current refs used inside event listeners (avoids stale closures)
  const fusionColormapRef = useRef(fusionColormap);
  const fusionOpacityRef = useRef(fusionOpacity);
  const selectedSeriesRef = useRef(selectedSeries);
  const isFusionActiveRef = useRef(isFusionActive);
  const activeViewportIndexRef = useRef(activeViewportIndex);
  // Tracks which specific viewport fusion is applied to — stays fixed after activation
  // so scroll-sync, controls, and mutual-exclusivity checks always target the right viewport
  // even when the user switches to a different active viewport.
  const fusionAppliedViewportRef = useRef(null);
  fusionColormapRef.current = fusionColormap;
  fusionOpacityRef.current = fusionOpacity;
  selectedSeriesRef.current = selectedSeries;
  isFusionActiveRef.current = isFusionActive;
  activeViewportIndexRef.current = activeViewportIndex;

  // Exclude the series currently displayed in the active viewport — fusing a
  // series with itself produces no useful result and confuses users.
  const activeStackFirstId =
    viewportSeriesData?.[activeViewportIndex]?.stack?.imageIds?.[0];
  const availableSeries = useMemo(
    () => selectFusableSeries({ studyData, priorStudyData, activeStackFirstId }),
    [studyData, priorStudyData, activeStackFirstId]
  );

  // ---- Locate the viewport-box DOM element for portal rendering ----
  // Anchored to the viewport where fusion was applied, not the current active viewport,
  // so the controls stay visible even when the user switches to another viewport.
  useEffect(() => {
    const viewportIndex =
      fusionAppliedViewportRef.current ?? preparingViewportRef.current;
    if ((isFusionActive || isPreparing) && viewportIndex != null) {
      const el = document.getElementById(`viewport_${viewportIndex}`);
      setPortalTarget(el || null);
    } else {
      setPortalTarget(null);
    }
    // isPreparing, not preparation: the target must not be recomputed on every
    // percent of progress.
  }, [isFusionActive, isPreparing]);

  // ---- Keep the PET overlay in sync as the user scrolls through CT slices ----
  // STACK_NEW_IMAGE fires once the new base image is on the actor but before the
  // viewport's render is queued, so rebuilding the overlay here puts both into
  // the same frame. IMAGE_RENDERED, which this used to listen to, fires after
  // that frame has been drawn — the base image would always be shown unfused for
  // a frame first, and longer than that whenever the overlay slice had to be
  // fetched.
  //
  // Mid-change the viewport has not adopted the new image yet, so the index and
  // the image both have to come from the event rather than from the viewport.
  // The listener is attached to the viewport where fusion was applied — not the
  // active viewport — so scrolling the fusion viewport always updates the overlay
  // regardless of which viewport is currently "active".
  useEffect(() => {
    if (!isFusionActive) return;

    const fusionViewportIdx = fusionAppliedViewportRef.current;
    if (fusionViewportIdx == null) return;

    let cancelled = false;
    let element = null;
    let vpRef = null;
    let lastCtIndex = -1;

    const onNewImage = (evt) => {
      if (cancelled || !vpRef || !isFusionActiveRef.current) return;
      // "None" selected — stay on plain grayscale, don't bring the overlay back.
      if (fusionColormapRef.current === FUSION_NONE_KEY) return;
      const series = selectedSeriesRef.current;
      if (!series?.imageIds) return;

      const ctIndex = evt?.detail?.imageIdIndex;
      if (ctIndex == null || ctIndex === lastCtIndex) return;

      lastCtIndex = ctIndex;
      applyPETFusion(
        vpRef,
        series.imageIds,
        fusionColormapRef.current,
        fusionOpacityRef.current,
        { baseImage: evt?.detail?.image }
      );
    };

    getViewport(fusionViewportIdx).then((vp) => {
      if (!vp || cancelled) return;
      vpRef = vp;
      element = vp.element;
      if (!element) return;
      // Seed with the index already displayed so the first STACK_NEW_IMAGE
      // after setup doesn't redundantly re-apply fusion for the current slice.
      lastCtIndex = vp.getCurrentImageIdIndex?.() ?? -1;
      element.addEventListener(Enums.Events.STACK_NEW_IMAGE, onNewImage);
    });

    return () => {
      cancelled = true;
      if (element) {
        element.removeEventListener(Enums.Events.STACK_NEW_IMAGE, onNewImage);
      }
    };
  }, [isFusionActive]); // activeViewportIndex intentionally omitted — fusion stays on its own viewport

  // ---- Track active viewport changes (fusion stays on its own viewport) ----
  useEffect(() => {
    prevViewportRef.current = activeViewportIndex;
  }, [activeViewportIndex]);

  // ---- When a new series is loaded into the fusion viewport (drag or click), clear fusion ----
  // Watch viewportSeriesData rather than activeViewportIndex so we track the viewport
  // where fusion was applied, not just whichever viewport is currently active.
  useEffect(() => {
    if (!isFusionActive || fusionAppliedViewportRef.current == null) return;
    const fusionViewportFirstId =
      viewportSeriesData?.[fusionAppliedViewportRef.current]?.stack?.imageIds?.[0];
    if (
      fusionBaseSeriesRef.current &&
      fusionViewportFirstId &&
      fusionViewportFirstId !== fusionBaseSeriesRef.current
    ) {
      // Block the scroll-sync listener immediately — don't wait for React to re-render
      isFusionActiveRef.current = false;
      // Nothing is going to display this series now — stop reading ahead for it.
      stopOverlayPrefetch(selectedSeriesRef.current?.imageIds);
      cancelPreparation();
      const vpIdx = fusionAppliedViewportRef.current;
      fusionAppliedViewportRef.current = null;
      (async () => {
        try {
          const vp = await getViewport(vpIdx);
          if (vp) removePETFusion(vp);
        } catch { /* ignore */ }
      })();
      setIsFusionActive(false);
      setSelectedSeries(null);
      setFusionColormap(DEFAULT_COLORMAP);
      setFusionOpacity(DEFAULT_OPACITY);
      setPortalTarget(null);
      fusionBaseSeriesRef.current = null;
    }
  }, [viewportSeriesData, isFusionActive]);

  // ---- When Color LUT activates on the SAME viewport, deactivate fusion ----
  // If Color LUT is applied to a different viewport, leave fusion intact.
  useEffect(() => {
    const handleColorLUTActivated = async (event) => {
      if (!isFusionActiveRef.current) return;
      const lutViewportIndex = event.detail?.viewportIndex;
      if (
        lutViewportIndex != null &&
        fusionAppliedViewportRef.current != null &&
        lutViewportIndex !== fusionAppliedViewportRef.current
      ) {
        return; // Color LUT is on a different viewport — keep fusion intact
      }
      isFusionActiveRef.current = false; // block scroll-sync listener immediately
      // Nothing is going to display this series now — stop reading ahead for it.
      stopOverlayPrefetch(selectedSeriesRef.current?.imageIds);
      cancelPreparation();
      const vpIdx = fusionAppliedViewportRef.current ?? activeViewportIndexRef.current;
      fusionAppliedViewportRef.current = null;
      try {
        const vp = await getViewport(vpIdx);
        if (vp) removePETFusion(vp);
      } catch { /* ignore */ }
      setIsFusionActive(false);
      setSelectedSeries(null);
      setFusionColormap(DEFAULT_COLORMAP);
      setFusionOpacity(DEFAULT_OPACITY);
      fusionBaseSeriesRef.current = null;
    };
    document.addEventListener(COLORLUT_ACTIVATED_EVENT, handleColorLUTActivated);
    return () => document.removeEventListener(COLORLUT_ACTIVATED_EVENT, handleColorLUTActivated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //  open the Fusion series-select modal from the bottom "More" menu ----
  // On mobile there is no toolbar icon; the More-menu tile dispatches this event.
  useEffect(() => {
    const handleToggle = () => setShowModal((prev) => !prev);
    document.addEventListener("toggleFusion", handleToggle);
    return () => document.removeEventListener("toggleFusion", handleToggle);
  }, []);

  /**
   * Apply PET fusion on the specified viewport (defaults to the active viewport).
   */
  const applyFusionToActiveViewport = useCallback(
    async (series, colormap, opacity, viewportIdx) => {
      const targetViewportIndex = viewportIdx != null ? viewportIdx : activeViewportIndex;
      try {
        if (!series?.imageIds || series.imageIds.length === 0) {
          showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.fusion-no-images", "Selected series has no images"));
          return false;
        }

        const viewport = await getViewport(targetViewportIndex);
        if (!viewport) {
          showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.fusion-no-viewport", "No active viewport found"));
          return false;
        }

        const outcome = await applyPETFusion(
          viewport,
          series.imageIds,
          colormap,
          opacity
        );

        if (!outcome?.ok) {
          // Name the failure. Reporting five different causes with one message
          // is what made every "fusion failed" report need a round of guessing.
          showToastv2(TOAST_TYPE.WARNING, fusionFailureMessage(outcome?.reason));
          return false;
        }

        return true;
      } catch (error) {
        console.error("Error applying fusion:", error);
        showToastv2(TOAST_TYPE.ERROR, t("viewer.tools.fusion-failed", "Failed to apply fusion"));
        return false;
      }
    },
    [activeViewportIndex, showToastv2, t]
  );

  /**
   * Handle series selection from the modal.
   * Closes modal, applies fusion with default colormap, shows viewport overlay.
   */
  const handleSeriesSelect = useCallback(
    async (series) => {
      setSelectedSeries(series);
      setShowModal(false);

      // Fall back to the default colormap if fusion was left on "None" — picking a
      // new series should show color, not silently stay in grayscale.
      const colormapToUse = isFusionActive && fusionColormap !== FUSION_NONE_KEY ? fusionColormap : DEFAULT_COLORMAP;
      const opacityToUse = isFusionActive ? fusionOpacity : DEFAULT_OPACITY;

      if (!isFusionActive || fusionColormap === FUSION_NONE_KEY) {
        setFusionColormap(DEFAULT_COLORMAP);
        setFusionOpacity(DEFAULT_OPACITY);
      }

      // Start pulling both series down, and put the loader up, BEFORE applying
      // fusion. Applying it is itself several round trips on a cold series —
      // measuring the overlay alone reads SERIES_PROFILE_SAMPLES slices — so a
      // loader raised afterwards appears only once the wait it describes is
      // over, which is the wrong way round.
      preparationRef.current?.cancel();
      // Claim the slot before starting. prepareFusionSeries reports its opening
      // figure synchronously, so the callback runs before the call returns —
      // anything it reads has to be in place first.
      const token = { cancel: () => {} };
      preparationRef.current = token;
      preparingViewportRef.current = activeViewportIndex;

      // The total is every slice of both series, so report what it is made of —
      // on its own a combined figure reads as a count of fused images.
      const baseStack = viewportSeriesData?.[activeViewportIndex]?.stack;
      const composition = {
        overlayModality: (series.seriesModality || series.modality || "").toUpperCase(),
        overlayCount: series.imageIds?.length ?? 0,
        baseModality: (baseStack?.seriesModality || baseStack?.modality || "").toUpperCase(),
        baseCount: baseStack?.imageIds?.length ?? 0,
      };

      let lastPercent = -1;
      const run = prepareFusionSeries(
        {
          overlayImageIds: series.imageIds,
          baseImageIds: baseStack?.imageIds,
        },
        (read, total) => {
          if (preparationRef.current !== token) return; // superseded
          if (read >= total) {
            setPreparation(null);
            return;
          }
          // One render per percent. A study is hundreds of slices, and a render
          // each would cost more than the download it is reporting.
          const percent = total ? Math.round((read / total) * 100) : 0;
          if (percent === lastPercent) return;
          lastPercent = percent;
          setPreparation({ read, total, ...composition });
        }
      );
      token.cancel = run.cancel;
      run.done.finally(() => {
        if (preparationRef.current === token) {
          preparationRef.current = null;
          preparingViewportRef.current = null;
          setPreparation(null);
        }
      });

      const success = await applyFusionToActiveViewport(series, colormapToUse, opacityToUse);
      if (!success) {
        // Nothing is going to be fused — take the loader down and stop reading.
        cancelPreparation();
      } else {
        isFusionActiveRef.current = true; // enable scroll-sync listener before next render
        fusionAppliedViewportRef.current = activeViewportIndex; // record which viewport fusion was applied to
        setIsFusionActive(true);
        // Record which base series the fusion was applied on, using imageIds[0] as the
        // stable identity key (always populated for any loaded series).
        fusionBaseSeriesRef.current =
          viewportSeriesData?.[activeViewportIndex]?.stack?.imageIds?.[0] ?? null;
        // Notify sibling tools (e.g. Color LUT) so they can deactivate if on the same viewport
        document.dispatchEvent(new CustomEvent(FUSION_ACTIVATED_EVENT, { detail: { viewportIndex: activeViewportIndex } }));

        showToastv2(TOAST_TYPE.SUCCESS, t("viewer.tools.fusion-applied", "Fusion applied — use controls at viewport bottom to adjust"));
      }
    },
    [applyFusionToActiveViewport, cancelPreparation, isFusionActive, fusionColormap, fusionOpacity, showToastv2, viewportSeriesData, activeViewportIndex]
  );

  /**
   * Change the colormap on an already-fused viewport (re-applies fusion).
   * Selecting "None" drops back to plain grayscale but keeps the Fusion tool
   * and its overlay controls active — use the panel's Close (X) button to
   * remove fusion entirely.
   */
  const handleOverlayColormapChange = useCallback(
    async (newColormap) => {
      setFusionColormap(newColormap);
      const vpIdx = fusionAppliedViewportRef.current ?? activeViewportIndex;
      if (newColormap === FUSION_NONE_KEY) {
        try {
          const vp = await getViewport(vpIdx);
          if (vp) removePETFusion(vp);
        } catch { /* ignore */ }
        return;
      }
      if (selectedSeries) {
        await applyFusionToActiveViewport(selectedSeries, newColormap, fusionOpacity, vpIdx);
      }
    },
    [applyFusionToActiveViewport, selectedSeries, fusionOpacity, activeViewportIndex]
  );

  /**
   * Change the opacity on an already-fused viewport (lightweight, no reload).
   */
  const handleOverlayOpacityChange = useCallback(
    async (newOpacity) => {
      setFusionOpacity(newOpacity);
      // Nothing rendered to update while "None" is selected — and re-applying
      // here would re-add the overlay via the "__none__" colormap fallback path.
      if (fusionColormap === FUSION_NONE_KEY) return;
      // Re-apply fusion with new opacity baked into the RGBA alpha channel.
      // StackViewport ignores actor-level opacity for overlay actors, so a
      // full re-apply is needed. The PET image is already cached so this is fast.
      if (selectedSeries) {
        await applyFusionToActiveViewport(selectedSeries, fusionColormap, newOpacity, fusionAppliedViewportRef.current);
      }
    },
    [applyFusionToActiveViewport, selectedSeries, fusionColormap]
  );

  /**
   * Remove fusion entirely.
   */
  const handleReset = useCallback(async () => {
    isFusionActiveRef.current = false; // block scroll-sync listener immediately
    // Nothing is going to display this series now — stop reading ahead for it.
    stopOverlayPrefetch(selectedSeriesRef.current?.imageIds);
    cancelPreparation();
    const vpIdx = fusionAppliedViewportRef.current ?? activeViewportIndex;
    fusionAppliedViewportRef.current = null;
    try {
      const viewport = await getViewport(vpIdx);
      if (viewport) removePETFusion(viewport);
    } catch { /* ignore */ }

    setIsFusionActive(false);
    setSelectedSeries(null);
    setFusionColormap(DEFAULT_COLORMAP);
    setFusionOpacity(DEFAULT_OPACITY);
    showToastv2(TOAST_TYPE.SUCCESS, t("viewer.tools.fusion-removed", "Fusion removed"));
  }, [activeViewportIndex, cancelPreparation, showToastv2, t]);

  function hideModal() {
    setShowModal(false);
  }

  const DefaultButtonList = [
    {
      title: t("button.close", "Close"),
      onclick: hideModal,
    },
  ];

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("viewer.tools.fusion-select-series", "Fusion — Select Series")}
        handleCancel={hideModal}
      />
    ),
    footer: (
      <ModalFooter
        PrimaryButtonList={[]}
        DefaultButtonList={DefaultButtonList}
      />
    ),
    body: (
      <ColorLUTBody
        availableSeries={availableSeries}
        onSeriesSelect={handleSeriesSelect}
        selectedSeriesId={selectedSeries?.seriesId}
      />
    ),
    width: "520px",
  };

  // Covers the fused viewport until both series are down. Scrolling before that
  // is what shows the base image on its own: the overlay for a slice that has
  // not been read yet cannot be drawn in the frame the base image is drawn in.
  const preparationLoader = preparation && (
    <div
      className="fusion-preparing"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="fusion-preparing-label">
        {t("viewer.tools.fusion-preparing", "Preparing fusion")}
      </span>
      <div className="fusion-preparing-track">
        <div
          className="fusion-preparing-bar"
          style={{
            width: `${preparation.total ? Math.round((preparation.read / preparation.total) * 100) : 0}%`,
          }}
        />
      </div>
      <span className="fusion-preparing-count">
        {t("viewer.tools.fusion-preparing-progress", "Downloaded {{loaded}} of {{total}} slices", {
          loaded: preparation.read,
          total: preparation.total,
        })}
      </span>
    </div>
  );

  //  on mobile there is no toolbar icon — the Fusion series-select modal is
  // opened from the bottom "More" menu via the `toggleFusion` event (listener above). We still
  // render the modal + the (mobile-friendly) fusion overlay, reusing all the same fusion logic.
  if (isMobile) {
    return (
      <>
        <ModalMain
          updatedModal={true}
          showModal={showModal}
          modalDetails={modalDetails}
        />
        {(isFusionActive || isPreparing) &&
          portalTarget &&
          createPortal(
            <>
              {preparationLoader}
              {isFusionActive && (
                <ColorLUTOverlay
                  fusionColormap={fusionColormap}
                  fusionOpacity={fusionOpacity}
                  onColormapChange={handleOverlayColormapChange}
                  onOpacityChange={handleOverlayOpacityChange}
                  onReset={handleReset}
                />
              )}
            </>,
            portalTarget
          )}
      </>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <CustomToolTipHost
        id="color-lut-tool"
        content={t("viewer.tools.fusion", "Fusion")}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
        styles={toolTipStyles}
        className="myToolTip"
      >
        <div className="highlight-tool">
          <div
            style={toolBarFlex}
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => setShowModal(!showModal)}
          >
            <ColorLutIcon
              color={
                hovered || showModal || isFusionActive
                  ? theme.palette.activeTool
                  : theme.palette.inactiveTool
              }
            />
          </div>
        </div>
      </CustomToolTipHost>

      <ModalMain
        updatedModal={true}
        showModal={showModal}
        modalDetails={modalDetails}
      />

      {/* Viewport-level overlay rendered via portal */}
      {(isFusionActive || isPreparing) &&
        portalTarget &&
        createPortal(
          <>
            {preparationLoader}
            {isFusionActive && (
              <ColorLUTOverlay
                fusionColormap={fusionColormap}
                fusionOpacity={fusionOpacity}
                onColormapChange={handleOverlayColormapChange}
                onOpacityChange={handleOverlayOpacityChange}
                onReset={handleReset}
              />
            )}
          </>,
          portalTarget
        )}
    </div>
  );
};

export default ColorLUTTool;
